import {ArtifactCard} from './ArtifactCard';
import {
  useAddUserArtifactPinMutation,
  useAddUserUrlPinMutation,
  useRemoveUserPinMutation,
  useGetUserByEmailQuery,
} from 'app/store/users/userApiSlice';
import {useSelector, useDispatch} from 'react-redux';
import {selectUser, setRecentlyViewed} from 'app/store/users/userSlice';
import {
  Custom_Encode_URL,
  IArtifact,
  IDocumentCard,
  IProcessCard,
  ITrainingCard,
  PinTypes,
  ProcessTabs,
} from 'app/types/Shared';
import React from 'react';
import {Snackbar, Typography, Stack} from '@mui/material';
import {Link} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {setCurrentMapXL, setProcessPageTab, setProcessPageTabIndex} from 'app/store/appState/appStateSlice';
import {selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import {useFeatureFlag} from 'configcat-react';

type CardProps = {
  data: ITrainingCard | IDocumentCard | IProcessCard;
  disabled?: boolean;
};

export const ArtifactCards: React.FC<CardProps> = ({data, disabled = false}: CardProps) => {
  const dispatch = useDispatch();
  const [userAddArtifactPin] = useAddUserArtifactPinMutation();
  const [userAddUrlPin] = useAddUserUrlPinMutation();
  const [userRemovePin] = useRemoveUserPinMutation();
  const user = useSelector(selectUser);
  const allProcess = useSelector(selectAllProcesses);
  const {trackEvent} = useMatomo();

  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);

  const getUserEmail = () => {
    if (user?.email) {
      return user.email;
    } else return '';
  };
  const {refetch} = useGetUserByEmailQuery(getUserEmail(), {skip: getUserEmail() === ''});
  const [clipBoard, setClipBoard] = React.useState(false);
  const navigate = useNavigate();

  const setPins = async (type: string, action: boolean, artifact: IArtifact) => {
    //Action:: true === set pin, false === remove pin
    if (action) {
      if (type === PinTypes.Process) {
        const userPin = user?.user_pin.find((pin) => pin.object_id === artifact.key);
        if (!userPin) {
          if (artifact.url && user?.id) {
            trackEvent({category: 'User Pin', action: 'Process Pin Created', name: artifact.key});
            await userAddUrlPin({
              url: artifact.url,
              userId: user?.id,
              key: artifact.key,
              type: PinTypes.Process,
            });
            await refetch();
          }
        }
      } else {
        if (user?.id) {
          const userPin = user?.user_pin.find((pin) => pin.object_id === artifact.key);
          if (!userPin && type === PinTypes.Artifact) {
            trackEvent({category: 'User Pin', action: 'Artifact Pin Created', name: artifact.key});
            await userAddArtifactPin({userId: user?.id, key: artifact.key, type: PinTypes.Artifact});
            await refetch();
          } else if (!userPin && type === PinTypes.Training) {
            trackEvent({category: 'User Pin', action: 'Training Pin Created', name: artifact.key});
            await userAddArtifactPin({userId: user?.id, key: artifact.key, type: PinTypes.Training});
            await refetch();
          }
        }
      }
    } else {
      if (user?.user_pin) {
        const userPin = user?.user_pin.find((pin) => pin.object_id === artifact.key);
        if (userPin) {
          trackEvent({category: 'User Pin', action: 'Pin Deleted', name: artifact.key});
          await userRemovePin({userId: userPin.user_id, key: userPin.object_id, pinId: userPin.id});
          await refetch();
        }
      }
    }
  };

  const getPinned = () => {
    if (user?.user_pin) {
      const artifactPin = user?.user_pin.find((pin) => pin.object_id === data.key);
      if (artifactPin) {
        return true;
      }
    }
    return false;
  };

  const setRecentlyViewedArtifact = (artifact: IArtifact) => {
    dispatch(setRecentlyViewed(artifact));
  };

  const Copy = (link: string) => {
    if (!navigator.clipboard) {
      console.log('Copy to clipboard failed: The context is not secure');
      console.log(`Link to copy is : ${link}`);
      return;
    }
    navigator.clipboard
      .writeText(link)
      .then(() => {
        console.log('Link copied to clipboard');
        setClipBoard(true);
      })
      .catch((error) => {
        console.error('Copy to clipboard failed:', error);
      });
  };

  const linkTo = (title: string, url: string) => {
    const process = allProcess?.find((process) => process.name === title && process.parent_process !== null);
    if (process && process.operating_models_list) {
      const dashIndex = url.lastIndexOf('/process');
      const substringURL = url.substring(dashIndex);
      dispatch(setProcessPageTab(ProcessTabs.Map));
      dispatch(setProcessPageTabIndex(isChangeNotificationsEnabled ? 1 : 0));
      dispatch(setCurrentMapXL(false));
      navigate(`${substringURL}`);
    } else {
      navigate(
        `/process/${Custom_Encode_URL(title)}/${
          process?.operating_models_list ? process.operating_models_list : 'Hybrid'
        }`,
      );
    }

    return Promise.resolve();
  };

  return (
    <>
      <ArtifactCard
        data={data}
        pinned={getPinned()}
        pinsCallback={setPins}
        setRecentlyViewed={setRecentlyViewedArtifact}
        copy={Copy}
        disabled={disabled}
        linkTo={linkTo}
        trackClick={trackEvent}
      />
      <Snackbar
        open={clipBoard}
        autoHideDuration={8000}
        onClose={() => setClipBoard(false)}
        message={
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-start'>
            <Link sx={{transform: 'rotate(-45deg)', color: 'white'}} />
            <Typography>Link Copied to Clipboard</Typography>
          </Stack>
        }
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      />
    </>
  );
};
